import { PartnerOrganization } from '@/modules/core/types/partnerType.type';
import useOrganizationName from '@/modules/my-account/hooks/useOrganizationName';
import { TitleInfo } from '@/modules/study-insights/views/StudyInsightsScreen/components/TitleInfo';
import { TFunction } from 'i18next';

interface TooltipByOrganizationProps {
  t: TFunction;
  configuredPartners: PartnerOrganization[];
  translationsKey: string;
  direction?: 'column' | 'row';
}

/*
 * TooltipByOrganization is a component to display tooltips according to an organization
 * and the translations keys from a locale file.
 * @param {TFunction} t - i18n function to translate the strings, should be related to the translations file from where we want to get the translations.
 * @param {PartnerOrganization[]} configuredPartners - List of organizations for which the tooltip has to be displayed.
 * @param {string} translationsKey - Key to get the translations from the translations file.
 * @param {string} organizationName - Name of the organization of the current logged user.
 * @param {'column' | 'row'} direction - Direction of the tooltip. - default is 'row' (from TitleInfo).
  
 *
 * For this component to work, the translations file should have the following structure:
 * 
 * Individual organization keys (backwards compatibility):
 * tooltips: {
 *  [organizationName_1]: {
 *   [translationsKey]: {
 *    title: 'Title of the tooltip',
 *    subtitle: 'Subtitle of the tooltip',
 *    tooltip: 'Tooltip content',
 *   },
 *  [organizationName_2]: {
 *   [translationsKey]: {
 *    title: 'Title of the tooltip',
 *    subtitle: 'Subtitle of the tooltip',
 *    tooltip: 'Tooltip content',
 *   },
 *  },
 * }
 */
const TooltipByOrganization = ({
  t,
  configuredPartners,
  translationsKey,
  direction,
}: TooltipByOrganizationProps): JSX.Element => {
  const organizationName = useOrganizationName();
  if (!organizationName) {
    return <></>;
  }

  if (!Object.values(configuredPartners).includes(organizationName as PartnerOrganization)) {
    return <></>;
  }

  const tooltipKey = organizationName.replace('helix-', ''); // Remove 'helix-' prefix if exists

  if (!tooltipKey) {
    return <></>;
  }

  return (
    <TitleInfo
      title={t(`tooltips.${tooltipKey}.${translationsKey}.title`)}
      content={t(`tooltips.${tooltipKey}.${translationsKey}.subtitle`)}
      tooltip={t(`tooltips.${tooltipKey}.${translationsKey}.tooltip`)}
      direction={direction}
    />
  );
};

export default TooltipByOrganization;
